import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API, fmtDate } from '@/lib/helpers';

const PRINT_HEADER_IMAGE_URL = 'https://customer-assets.emergentagent.com/job_592478ef-3239-4a11-a717-ef29df984854/artifacts/2j3j80jv_Captura%20de%20Tela%202026-06-25%20a%CC%80s%2022.47.51.png';
const NOT_INFORMED = 'Não informado';

const stripHtml = (html = '') => {
  const div = document.createElement('div');
  div.innerHTML = html || '';
  return (div.textContent || div.innerText || '').replace(/\s+/g, ' ').trim();
};

const hasHtmlText = (html = '') => stripHtml(html).length > 0;

const normalizePrintHtml = (html = '') => String(html || '')
  .replace(/(<p>(\s|&nbsp;|<br\s*\/?\s*>)*<\/p>\s*)+$/gi, '')
  .replace(/(<br\s*\/?\s*>\s*)+$/gi, '')
  .trim();

function CollaboratorsText({ names = [], officialName = '', groupName = '' }) {
  const clean = names.map(name => String(name || '').trim()).filter(Boolean);
  const official = String(officialName || '').trim();
  const finalNames = clean.length ? clean : (official ? [official] : []);
  if (!finalNames.length) return <>{NOT_INFORMED}</>;
  return <>{finalNames.map((name, index) => {
    const separator = index === 0 ? '' : (index === finalNames.length - 1 ? ' e ' : ', ');
    const highlighted = official && name.toLowerCase() === official.toLowerCase();
    return <span key={`${name}-${index}`}>{separator}{highlighted ? <strong className="official-storyteller-print">{name}</strong> : name}</span>;
  })}{groupName ? ` (${groupName})` : ''}</>;
}

function sanitizeTitlePart(value = '') {
  return String(value || '').replace(/[\\/:*?"<>|]+/g, '').replace(/\s+/g, ' ').trim();
}

function compactDateYYMMDD(value = '') {
  const raw = String(value || '').split('T')[0];
  const parts = raw.split('-');
  if (parts.length === 3) return `${parts[0].slice(-2)}${parts[1].padStart(2, '0')}${parts[2].padStart(2, '0')}`;
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) return `${String(parsed.getFullYear()).slice(-2)}${String(parsed.getMonth() + 1).padStart(2, '0')}${String(parsed.getDate()).padStart(2, '0')}`;
  return '';
}

function PrintField({ label, children, grow = false }) {
  return <div className={`print-field ${grow ? 'grow' : ''}`}><strong>{label}</strong>{children}</div>;
}

function Section({ title, children, minHeight = 0 }) {
  return <section className="print-section"><div className="section-title">{title}</div><div className="section-body" style={{ minHeight }}>{children || <span className="placeholder">&nbsp;</span>}</div></section>;
}

function HtmlSectionContent({ html, fallback, className = '' }) {
  const normalizedHtml = normalizePrintHtml(html);
  return hasHtmlText(normalizedHtml) ? <div className={`print-html ${className}`} dangerouslySetInnerHTML={{ __html: normalizedHtml }} /> : <span>{fallback}</span>;
}

const valueOrNotInformed = (value) => {
  const normalized = String(value || '').trim();
  return normalized || NOT_INFORMED;
};

export default function EbiStoryPrintPage({ publicMode = false }) {
  const { storyId, slug } = useParams();
  const navigate = useNavigate();
  const [story, setStory] = useState(null);
  const [user, setUser] = useState(null);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [headerLoaded, setHeaderLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      if (publicMode) {
        const publicR = await fetch(`${API}/ebi/public/${slug}/stories/${storyId}/print`);
        if (!publicR.ok) throw new Error('História não encontrada ou não aprovada');
        const data = await publicR.json();
        setUser({ church_name: data.church_name, church_city: data.church_city, church_state: data.church_state });
        setStory(data.story);
        setPeople(data.people || []);
      } else {
        const [meR, storyR, peopleR] = await Promise.all([
          fetch(`${API}/auth/me`, { credentials: 'include' }),
          fetch(`${API}/ebi/stories/${storyId}`, { credentials: 'include' }),
          fetch(`${API}/ebi/people`, { credentials: 'include' }).catch(() => null),
        ]);
        if (!meR.ok) { navigate('/'); return; }
        if (!storyR.ok) throw new Error('História não encontrada');
        setUser(await meR.json());
        setStory(await storyR.json());
        if (peopleR?.ok) setPeople(await peopleR.json());
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, publicMode, slug, storyId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!story || !user) return;
    const title = sanitizeTitlePart(story.title || 'História');
    const church = sanitizeTitlePart(user.church_name || 'Igreja');
    const date = compactDateYYMMDD(story.telling_date || story.created_at);
    document.title = `EBI ${church} - ${title}${date ? ` - ${date}` : ''}`;
  }, [story, user]);
  useEffect(() => {
    if (!loading && story && headerLoaded) {
      const timer = setTimeout(() => window.print(), 450);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [headerLoaded, loading, story]);

  const collaboratorNames = useMemo(() => {
    const groupId = story?.group_id;
    const names = people.filter(p => !p.deleted && p.active && groupId && (p.group_ids || []).includes(groupId)).map(p => p.name).filter(Boolean);
    return names.length ? names : [];
  }, [people, story]);

  if (loading) return <div className="print-loading" data-testid="ebi-story-print-loading" />;
  if (!story) return <div className="print-loading">História não encontrada.</div>;

  const localParts = [user?.church_name, user?.church_city, user?.church_state].map(item => String(item || '').trim()).filter(Boolean);
  const localLine = localParts.length ? localParts.join(' - ') : NOT_INFORMED;

  return (
    <div className="print-page-wrap" data-testid="ebi-story-print-page">
      <style>{`
        @page { size: A4; margin: 8mm; }
        * { box-sizing: border-box; }
        html, body, .print-page, .print-page * { -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact; }
        body { margin: 0; background: #e5e7eb; font-family: Arial, Helvetica, sans-serif; color: #000; }
        .print-loading { min-height: 100vh; display: flex; align-items: center; justify-content: center; font-family: Arial, sans-serif; }
        .print-page-wrap { padding: 18px; }
        .print-page { --print-border-width: 1pt; --print-border: var(--print-border-width) solid #000; width: 210mm; min-height: 297mm; margin: 0 auto; background: #fff; padding: 8mm; font-size: 9.2pt; line-height: 1.25; }
        .header-image { display: block; width: 100%; height: auto; margin: 0 0 12px; padding: 0; }
        .block-title, .section-title { border: var(--print-border); border-top: 0; background: #d9d9d9; text-align: center; font-weight: 700; padding: 3px 5px; font-size: 9pt; }
        .first-block-title { border-top: var(--print-border); }
        .script-row { border: var(--print-border); border-top: 0; display: grid; grid-template-columns: 1fr 40mm; min-height: 23px; }
        .print-field { padding: 5px 7px; border-right: var(--print-border); }
        .print-field:last-child { border-right: 0; }
        .print-field strong { margin-right: 4px; }
        .general { border-left: var(--print-border); border-right: var(--print-border); }
        .general .line { border-bottom: var(--print-border); padding: 5px 7px; min-height: 25px; }
        .official-storyteller-print { font-weight: 700; text-decoration: underline; }
        .print-section .section-title { margin-top: 0; border-bottom: 0; }
        .section-body { border: var(--print-border); padding: 7px; white-space: normal; overflow-wrap: anywhere; box-decoration-break: clone; -webkit-box-decoration-break: clone; }
        .print-html { white-space: normal; overflow-wrap: anywhere; }
        .print-html p { margin: 0; }
        .print-html br { display: block; content: ""; min-height: 1.28em; }
        .print-html p + p { margin-top: 4px; }
        .print-html h1 { font-size: 13pt; font-weight: 700; margin: 0 0 5px; }
        .print-html h2 { font-size: 11.5pt; font-weight: 700; margin: 0 0 4px; }
        .print-html h3 { font-size: 10pt; font-weight: 700; margin: 0 0 3px; }
        .print-html h1 + p, .print-html h2 + p, .print-html h3 + p { margin-top: 3px; }
        .print-html ul { list-style-type: disc !important; list-style-position: outside; margin: 0 0 0 20px; padding-left: 14px; }
        .print-html ol { list-style-type: decimal !important; list-style-position: outside; margin: 0 0 0 20px; padding-left: 14px; }
        .print-html ul ul { list-style-type: circle !important; }
        .print-html ul ul ul { list-style-type: square !important; }
        .print-html ol ol { list-style-type: lower-alpha !important; }
        .print-html li { display: list-item !important; margin: 0 0 2px; padding-left: 2px; }
        .print-html li p { margin: 0; display: inline; }
        .print-html ul[data-type="taskList"] { list-style: none !important; margin-left: 0; padding-left: 0; }
        .print-html ul[data-type="taskList"] li { display: flex !important; gap: 6px; align-items: flex-start; }
        .print-html ul[data-type="taskList"] li p { display: block; }
        .print-html blockquote { border-left: var(--print-border); margin: 4px 0; padding-left: 8px; font-style: italic; }
        .print-html mark { padding: 0 2px; box-decoration-break: clone; -webkit-box-decoration-break: clone; }
        .print-html table { width: 100%; border-collapse: collapse; table-layout: fixed; margin: 4px 0; }
        .print-html th, .print-html td { border: var(--print-border); padding: 3px 4px; vertical-align: top; }
        .print-html > :last-child { margin-bottom: 0 !important; }
        .print-html strong, .print-html b { font-weight: 700; }
        .print-html em, .print-html i { font-style: italic; }
        .print-html u { text-decoration: underline; }
        .story-body { font-size: 9pt; line-height: 1.28; }
        .footer-note { margin-top: 8px; text-align: center; font-size: 10px; line-height: 1.35; }
        .footer-highlight { color: #108f01; background: #f5f5f5; padding: 1px 3px; box-decoration-break: clone; -webkit-box-decoration-break: clone; }
        .footer-code { text-align: right; font-size: 7pt; font-weight: 700; margin-top: 3px; }
        @media print { body { background: #fff; } .print-page-wrap { padding: 0; } .print-page { width: auto; min-height: auto; margin: 0; padding: 0; box-shadow: none; } .block-title, .section-title { background-color: #d9d9d9 !important; } .section-body { -webkit-box-decoration-break: clone; box-decoration-break: clone; outline: 0.01pt solid transparent; box-shadow: inset -0.35pt 0 0 #000, inset 0 -0.35pt 0 #000; } .footer-highlight { background-color: #f5f5f5 !important; color: #108f01 !important; } }
      `}</style>
      <main className="print-page">
        <img className="header-image" src={PRINT_HEADER_IMAGE_URL} alt="Cabeçalho FOR.MIN.EBI.05 - Modelo de Formatação da História" onLoad={() => setHeaderLoaded(true)} onError={() => setHeaderLoaded(true)} data-testid="ebi-story-print-header-image" />
        <div className="block-title first-block-title">ROTEIRO DE APRESENTAÇÃO</div>
        <div className="script-row"><PrintField label="LOCAL:">{localLine}</PrintField><PrintField label="DATA:">{story.telling_date ? fmtDate(story.telling_date) : NOT_INFORMED}</PrintField></div>
        <div className="block-title">ORGANIZAÇÃO GERAL</div>
        <div className="general">
          <div className="line"><strong>Colaboradoras:</strong> <CollaboratorsText names={collaboratorNames} officialName={story.official_storyteller} groupName={story.group_name} /></div>
          <div className="line"><strong>Recepcionista(s):</strong> {NOT_INFORMED}</div>
          <div className="line"><strong>Título da História:</strong> {valueOrNotInformed(story.title)}</div>
          <div className="line"><strong>Referência da Bíblia (livro, capítulo e versículo):</strong> {valueOrNotInformed(story.biblical_reference)}</div>
          <div className="line"><strong>Fundamento Bíblico:</strong> {valueOrNotInformed(stripHtml(story.foundation))}</div>
        </div>
        <Section title="OBJETIVOS" minHeight={20}><HtmlSectionContent html={story.objective} fallback={NOT_INFORMED} /></Section>
        <Section title="PASSAGEM BÍBLICA/ HISTÓRIA" minHeight={250}><HtmlSectionContent html={story.story_html} fallback={NOT_INFORMED} className="story-body" /></Section>
        <Section title="ATIVIDADES" minHeight={28}><HtmlSectionContent html={story.activities} fallback={NOT_INFORMED} /></Section>
        <Section title="GLOSSÁRIO (se necessário)" minHeight={20}><HtmlSectionContent html={story.glossary} fallback={NOT_INFORMED} /></Section>
        <div className="footer-note"><span className="footer-highlight"><strong>Documento público</strong>, de uso exclusivo da Congregação Cristã no Brasil, com autorização para publicação e compartilhamento, desde que seu conteúdo seja preservado e a fonte devidamente citada. <strong>CÓPIAS SÃO PERMITIDAS</strong> e não violam as normas institucionais.</span></div>
        <div className="footer-code">FOR.MIN.EBI.05_REV00</div>
      </main>
    </div>
  );
}